import type {SiteConfig, SiteMap} from '~/interfaces/multisite';
import {useSiteDataStore} from '~/store/siteDataStore';
import {reactive} from 'vue';

const getHost = (url: URL | undefined): string => {
    return `${url?.protocol}//${url?.hostname}${url?.port ? `:${url?.port}` : ''}`;
};

const getSiteConfig = (sites: SiteMap, url: URL, fallback: SiteConfig): SiteConfig => {
    return sites[url.host] ?? sites[url.hostname] ?? fallback;
};

export const useMultisite = () => {
    const siteData = useSiteDataStore();
    const runtimeConfig = useRuntimeConfig() as {
        public: {
            sites: SiteMap;
            defaults: SiteConfig;
        };
    };

    const url = useRequestURL();

    const configuredSite = computed<SiteConfig>(() =>
        getSiteConfig(runtimeConfig.public.sites, url, runtimeConfig.public.defaults)
    );

    const siteName = computed(() => toValue(siteData.assets)?.siteName ?? configuredSite.value.name);

    const activeSite = computed<SiteConfig>(() => ({
        ...configuredSite.value,
        seo: {
            ...configuredSite.value.seo,
            prepend: `- ${siteName.value}`,
        },
    }));

    const variables = reactive({
        site: [activeSite.value.handle],
    });

    const featureFlag = (featureId: string): boolean => {
        return configuredSite.value.enable?.includes(featureId) ?? false;
    };

    const getSiteId = async(handle: string): Promise<string | undefined> => {
        const sites = siteData.sites.value ?? [];
        const site = sites.find((siteConfig: SiteConfig) => siteConfig.handle === handle);
        return site?.id;
    };
    return {
        sites: runtimeConfig.public.sites,
        siteIds: siteData?.sites?.map((site: SiteConfig) => ({id: site.id, handle: site.handle})),
        activeSite,
        host: getHost(url),
        variables,
        featureFlag,
        getSiteId,
    };
};
